import { BillingInfoDto } from "../dto/createPayment.dto";
import { ProductUsageDto } from "../dto/getActiveSubscriptionUsageDto.dto";
import { Role } from "./auth.model";
import { AssetType, BillingIntervalInMonths, PaymentStatus } from "./subscription.model";

export enum UserType {
  superAdmin = "superAdmin",
  customer = "customer",
  readOnlyAdmin = "readOnlyAdmin"
}

export enum EntityType {
  individual = "individual",
  corporate = "corporate"
}

export enum Product {
  default = "default",
  swap = "swap",
  fusion = "fusion",
  fusionPlus = "fusion-plus",
  balance = "balance",
  price = "price",
  orderbook = "orderbook",
  token = "token",
  txGateway = "tx-gateway",
  portfolio = "portfolio",
  gasPrice = "gas-price",
  nft = "nft",
  traces = "traces",
  history = "history",
  web3 = "web3",
  domain = "domains",
  tokenDetails = "token-details"
}

export const purchasableProducts = Object.values(Product).filter((product) => product !== Product.default);

export const productPathMapPublic: Record<string, Product> = {
  "^/": Product.default,
  "/orderbook": Product.orderbook,
  "/swap": Product.swap,
  "/fusion-plus": Product.fusionPlus,
  "/fusion": Product.fusion,
  "/balance": Product.balance,
  "/price": Product.price,
  "/token": Product.token,
  "/tx-gateway": Product.txGateway,
  "/portfolio": Product.portfolio,
  "/gas-price": Product.gasPrice,
  "/nft": Product.nft,
  "/traces": Product.traces,
  "/history": Product.history,
  "/web3": Product.web3,
  "/domain": Product.domain,
  "/token-details": Product.tokenDetails
};

export const mappedNamePublic: Record<Product, string> = {
  [Product.default]: "Rest of the APIs",
  [Product.orderbook]: "Orderbook API",
  [Product.swap]: "Swap API",
  [Product.balance]: "Balance API",
  [Product.price]: "Spot Price API",
  [Product.token]: "Token API",
  [Product.txGateway]: "Transaction API",
  [Product.portfolio]: "Portfolio API",
  [Product.gasPrice]: "Gas Price API",
  [Product.nft]: "NFT API",
  [Product.traces]: "Traces API",
  [Product.fusion]: "Fusion API",
  [Product.fusionPlus]: "Fusion Plus API",
  [Product.history]: "History API",
  [Product.web3]: "Web3 RPC API",
  [Product.domain]: "Domain API",
  [Product.tokenDetails]: "Token Details API"
};

export const mappedDescriptionPublic: Record<Product, string> = {
  [Product.default]: "api-product.default.description",
  [Product.orderbook]: "api-product.orderbook.description",
  [Product.swap]: "api-product.swap.description",
  [Product.balance]: "api-product.balance.description",
  [Product.price]: "api-product.price.description",
  [Product.token]: "api-product.token.description",
  [Product.txGateway]: "api-product.txGateway.description",
  [Product.portfolio]: "api-product.portfolio.description",
  [Product.gasPrice]: "api-product.gasPrice.description",
  [Product.nft]: "api-product.nft.description",
  [Product.traces]: "api-product.traces.description",
  [Product.fusion]: "api-product.fusion.description",
  [Product.fusionPlus]: "api-product.fusionPlus.description",
  [Product.history]: "api-product.history.description",
  [Product.web3]: "api-product.web3.description",
  [Product.domain]: "api-product.domain.description",
  [Product.tokenDetails]: "api-product.tokenDetails.description"
};

export type TopUpItem = {
  product: Product;
  quantity: number;
};

export const topUpPricePerUnit = 40;
export const topUpStepUnit = 100_000;

export type InvoiceModel = {
  documentId: bigint;
  issuedOn: string;
  paymentDue: string;
  paidOnDate: string | undefined;
  paymentStatus: PaymentStatus;
  walletAddress: string;
  paymentMethod: AssetType;
  quantity: number;
  amount: string;
  planName: string;
  billingIntervalInMonths: BillingIntervalInMonths;
  billingInfo: BillingInfoDto;
  isPaid: boolean;
  isOverdue: boolean;
  isUnpaid: boolean;
  isCanceled: boolean;
  isOrganization: boolean;
  paymentLink: string;
  isRenewal: boolean;
  description?: string | null | undefined;
  renewalDate?: string | null | undefined;
};

export type UserRoleInvite = {
  userEmail: string;
  newUserRole?: Role;
  oldUserRole?: Role | null;
  roleEditing: boolean;
  loginUrl: string;
};

export type VerificationAction = {
  kycLink: string;
};

export enum TemplateType {
  INVOICE = "invoice-pdf",
  INVOICE_MAIL = "invoice",
  RECEIPT = "receipt",
  SUBSCRIPTION_CONFIRMATION = "subscription-confirmation",
  MAGIC_LINK = "magic-link",
  SUBSCRIPTION_CANCELATION = "subscription-cancelation",
  SUBSCRIPTION_SUSPENSION = "subscription-suspension",
  SUBSCRIPTION_END_TRIAL = "subscription-end-trial",
  SUBSCRIPTION_DOWNGRADE = "subscription-downgrade",
  SUBSCRIPTION_DOWNGRADE_ORDER_CREATED = "subscription-downgrade-order-created",
  SUBSCRIPTION_UPGRADE = "subscription-upgrade",
  LEGAL_DOCS_UPDATE = "terms-update",
  LEGAL_DOCS_UPDATE_CORRECT_EFFECTIVE_DATE = "terms-update-correction-effective-date",
  EXPLICIT_COMMERCIAL_CONSENT = "terms-explicit-update",
  PAYMENT_REMINDER = "payment-reminder",
  GET_IN_TOUCH = "get-in-touch",
  INVITE_USER = "invite-user",
  REMOVE_USER = "remove-user",
  WALLET_BLACKLIST = "wallet-blacklist",
  COMPLIANCE_TEAM_WALLET = "compliance-team-wallet",
  COMPLIANCE_TEAM_AML = "compliance-team-aml",
  VERIFICATION_APPROVED = "verification-approved",
  VERIFICATION_REJECTED = "verification-rejected",
  VERIFICATION_ACTION = "verification-action",
  VERIFICATION_INVITE_FREE_PLAN = "verification-free-kyc",
  VERIFICATION_INVITE_PAID_PLAN = "kybVerificationUpgrade",
  SURVEY_INVITE = "survey-invite",
  SURVEY_REMINDER = "survey-reminder"
}

export enum Environment {
  DEVELOPMENT = "development",
  PRODUCTION = "production",
  STAGING = "staging"
}

export type Limits = Omit<ProductUsageDto, "isTopUp">;
